import { DocumentSection, EntityDocument } from '@api/api/document-entity';
import { Accordion } from '@design/components/accordion/Accordion';
import { Drawer } from '@design/components/drawer/Drawer';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { TrackingMetadata } from '@terminal/services/tracking/posthog/constants';
import { DocumentViewer } from '@terminal/views/alfred/document-search/document-viewer/DocumentViewer';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './CitationsAccordionWidget.module.scss';
import { Citations } from './components/citations/Citations';

export interface CitationsAccordionWidgetProps {
  citations: DocumentSection[];
  trackingMetadata?: TrackingMetadata;
}

export const CitationsAccordionWidget = ({
  citations,
}: CitationsAccordionWidgetProps) => {
  const { t } = useTranslation(['widget']);
  const citationsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<EntityDocument>();
  const [activeCitationIdx, setActiveCitationIdx] = useState<number>();

  const onClickCitation = (citationId: string, document: EntityDocument) => {
    const citationIdx = citations.findIndex(c => c.id === citationId);

    if (citationIdx === -1) {
      setActiveCitationIdx(undefined);
      setSelectedDoc(undefined);
      return;
    }

    setActiveCitationIdx(citationIdx);
    setSelectedDoc(document);
  };

  const onClose = () => {
    setSelectedDoc(undefined);
    setActiveCitationIdx(undefined);
  };

  const items = [
    {
      title: t('widget:citations.label'),
      content: (
        <ErrorBoundaryWrapper>
          <Citations
            citations={citations}
            onClick={(citationId, doc) => onClickCitation(citationId, doc)}
          />
        </ErrorBoundaryWrapper>
      ),
    },
  ];

  const page =
    activeCitationIdx !== undefined
      ? citations[activeCitationIdx]?.tags?.page
      : null;

  const getHighlightTexts = () => {
    const citation =
      activeCitationIdx !== undefined ? citations[activeCitationIdx] : null;

    if (!selectedDoc || selectedDoc.kind === 'presentation') {
      return [];
    }

    return [
      {
        text: citation
          ? citation.content
              .trim()
              .replace(/(\*\*)(.*?)\1/g, '$2')
              .replace(/\s+/g, ' ')
          : '',
        pages: page ? [parseInt(page)] : undefined,
        isPrimary: true,
      },
    ];
  };

  if (!citations.length) {
    return null;
  }

  return (
    <>
      <Accordion className={styles.styledAccordion} items={items} />
      {!!selectedDoc && (
        <Drawer
          className={styles.styledDrawer}
          isOpen
          handleCloseClick={onClose}
        >
          <ErrorBoundaryWrapper>
            <div className={styles.drawerSectionWrapper}>
              <div className={styles.drawerSection}>
                <h2 className={styles.citationsHeaderTitle}>
                  {t(
                    'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.citations'
                  )}
                </h2>
                <Citations
                  citations={citations}
                  onClick={onClickCitation}
                  activeIdx={activeCitationIdx}
                  ref={citationsRefs}
                />
              </div>
              <div className={styles.drawerSection}>
                {!!selectedDoc && (
                  <DocumentViewer
                    highlightTexts={getHighlightTexts()}
                    selectedCitation={{
                      document_id: selectedDoc.id,
                      document_name: selectedDoc.name,
                      document_type: selectedDoc.type,
                      pages: page ? [parseInt(page)] : [],
                    }}
                  />
                )}
              </div>
            </div>
          </ErrorBoundaryWrapper>
        </Drawer>
      )}
    </>
  );
};
